// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState } from 'react'
import { Sprout, SolarPanel, Waves, Droplets, Fuel, Zap, Battery, ArrowDownToLine, AirVent, Pipette, Heater, PlugZap, Home, Flame, ShieldCheck, Filter, Wrench, Camera, Bell, Lock, AlertTriangle } from 'lucide-react'
import { supabase } from '../supabase'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA',
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px', fontFamily: 'Georgia, serif',
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)',
    border: `1px solid ${C.border}`,
  },
  header: {
    padding: '24px 28px 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center',
  },
  title: { fontSize: '22px', fontWeight: '700', color: C.brown },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '22px',
    cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1,
  },
  body: { padding: '20px 28px 28px' },
  label: {
    fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', marginBottom: '5px', display: 'block',
    fontFamily: 'Calibri, sans-serif',
  },
  input: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '15px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', transition: 'border-color 0.2s',
  }),
  select: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '15px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', cursor: 'pointer',
    appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239A7B68' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
    paddingRight: '36px',
  }),
  textarea: (focused) => ({
    width: '100%', background: C.bg,
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '8px', padding: '11px 14px', fontSize: '15px',
    fontFamily: 'Georgia, serif', color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', resize: 'vertical', minHeight: '80px',
  }),
  row: { display: 'flex', gap: '12px' },
  field: { flex: 1 },
  typeGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px', marginBottom: '20px',
  },
  typeCard: (selected) => ({
    background: selected ? '#FBF3E8' : C.bg,
    border: `1px solid ${selected ? C.amber : C.border}`,
    borderRadius: '10px', padding: '14px 8px', cursor: 'pointer',
    textAlign: 'center', transition: 'all 0.2s',
  }),
  typeIcon: { display: 'flex', justifyContent: 'center', marginBottom: '6px' },
  typeName: (selected) => ({
    fontSize: '11px', color: selected ? C.amber : C.brown,
    fontWeight: selected ? '700' : '400',
    fontFamily: 'Calibri, sans-serif', lineHeight: 1.3,
  }),
  aduBadge: {
    fontSize: '9px', background: C.amber, color: C.card,
    borderRadius: '4px', padding: '1px 5px', marginTop: '3px',
    fontFamily: 'Calibri, sans-serif', letterSpacing: '0.5px',
    display: 'inline-block',
  },
  btn: (disabled) => ({
    width: '100%',
    background: disabled ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '14px',
    color: disabled ? C.muted : C.card, fontSize: '14px', fontWeight: '700',
    letterSpacing: '2px', textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Georgia, serif', marginTop: '6px',
  }),
  cancelBtn: {
    width: '100%', background: 'transparent',
    border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px',
    color: C.muted, fontSize: '13px', letterSpacing: '1px',
    textTransform: 'uppercase', cursor: 'pointer',
    fontFamily: 'Georgia, serif', marginTop: '10px',
  },
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '12px',
    fontFamily: 'Calibri, sans-serif',
  },
  stepBar: { display: 'flex', gap: '6px', marginBottom: '20px' },
  stepBarItem: (active) => ({
    flex: 1, height: '3px', borderRadius: '2px',
    background: active ? C.amber : C.border, transition: 'all 0.3s',
  }),
  aduInfoBox: {
    background: '#FBF3E8', border: `1px solid ${C.amber}44`,
    borderRadius: '10px', padding: '14px 16px', marginBottom: '20px',
    fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif',
    lineHeight: 1.6,
  },
}

export const systemTypes = [
  { id: 'septic', Icon: Sprout, name: 'Septic System' },
  { id: 'solar', Icon: SolarPanel, name: 'Solar Panels' },
  { id: 'pool', Icon: Waves, name: 'Pool / Spa' },
  { id: 'well', Icon: Droplets, name: 'Well Water' },
  { id: 'propane', Icon: Fuel, name: 'Propane Tank' },
  { id: 'generator', Icon: Zap, name: 'Generator' },
  { id: 'battery', Icon: Battery, name: 'Whole-Home Battery' },
  { id: 'sump_pump', Icon: ArrowDownToLine, name: 'Sump Pump' },
  { id: 'whole_house_fan', Icon: AirVent, name: 'Whole-House Fan' },
  { id: 'water_softener', Icon: Pipette, name: 'Water Softener' },
  { id: 'radiant_heat', Icon: Heater, name: 'Radiant Heat' },
  { id: 'electrical_panel', Icon: PlugZap, name: 'Electrical Panel' },
  { id: 'fireplace', Icon: Flame, name: 'Fireplace' },
  { id: 'fire_sprinkler', Icon: ShieldCheck, name: 'Fire Sprinkler System' },
  { id: 'air_filtration', Icon: Filter, name: 'Air Filtration / HEPA' },
  { id: 'security_system', Icon: ShieldCheck, name: 'Security System' },
  { id: 'security_cameras', Icon: Camera, name: 'Security Cameras' },
  { id: 'smart_doorbell', Icon: Bell, name: 'Smart Doorbell' },
  { id: 'smart_locks', Icon: Lock, name: 'Smart Locks' },
  { id: 'smoke_co_detectors', Icon: AlertTriangle, name: 'Smoke & CO Detectors' },
  { id: 'adu', Icon: Home, name: 'ADU', badge: 'Coming Soon' },
  { id: 'other', Icon: Wrench, name: 'Other' },
]

export const systemSchedules = {
  septic: [
    { task: 'Tank Pumping', interval: 'Every 3–5 years', type: 'contractor' },
    { task: 'Inspection & Baffle Check', interval: 'Every 3 years', type: 'contractor' },
    { task: 'Drain Field Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Bacteria Treatment', interval: 'Monthly', type: 'auto_order' },
  ],
  solar: [
    { task: 'Panel Cleaning', interval: 'Every 6 months', type: 'diy' },
    { task: 'Inverter Check', interval: 'Annually', type: 'diy' },
    { task: 'Full System Inspection', interval: 'Every 2 years', type: 'contractor' },
    { task: 'Monitor Production Output', interval: 'Monthly', type: 'diy' },
  ],
  pool: [
    { task: 'Chemical Balance Check', interval: 'Weekly', type: 'diy' },
    { task: 'Filter Cleaning', interval: 'Monthly', type: 'diy' },
    { task: 'Pump & Motor Inspection', interval: 'Annually', type: 'contractor' },
    { task: 'Shock Treatment', interval: 'Monthly', type: 'auto_order' },
    { task: 'Winter Winterization', interval: 'Annually — Fall', type: 'contractor' },
  ],
  well: [
    { task: 'Water Quality Test', interval: 'Annually', type: 'contractor' },
    { task: 'Pressure Tank Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Well Cap Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Pump System Inspection', interval: 'Every 2 years', type: 'contractor' },
  ],
  propane: [
    { task: 'Tank Level Check', interval: 'Monthly', type: 'diy' },
    { task: 'Regulator & Line Inspection', interval: 'Annually', type: 'contractor' },
    { task: 'Leak Test', interval: 'Annually', type: 'contractor' },
    { task: 'Tank Recertification', interval: 'Every 10 years', type: 'contractor' },
  ],
  generator: [
    { task: 'Oil & Filter Change', interval: 'Every 100 hours or annually', type: 'diy' },
    { task: 'Spark Plug Replacement', interval: 'Annually', type: 'diy' },
    { task: 'Battery Check', interval: 'Every 6 months', type: 'diy' },
    { task: 'Load Test Run', interval: 'Monthly', type: 'diy' },
    { task: 'Full Service Inspection', interval: 'Annually', type: 'contractor' },
  ],
  battery: [
    { task: 'Monitor Charge Cycles', interval: 'Monthly', type: 'diy' },
    { task: 'Software / Firmware Update', interval: 'As released', type: 'diy' },
    { task: 'Connection & Wiring Inspection', interval: 'Annually', type: 'contractor' },
    { task: 'Full System Performance Test', interval: 'Annually', type: 'contractor' },
  ],
  sump_pump: [
    { task: 'Test Pump Operation', interval: 'Every 3 months', type: 'diy' },
    { task: 'Clean Pump & Pit', interval: 'Annually', type: 'diy' },
    { task: 'Check Backup Battery', interval: 'Every 6 months', type: 'diy' },
    { task: 'Inspect Discharge Line', interval: 'Annually', type: 'diy' },
  ],
  whole_house_fan: [
    { task: 'Belt & Pulley Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Lubricate Bearings', interval: 'Annually', type: 'diy' },
    { task: 'Clean Louvers & Shutters', interval: 'Every 6 months', type: 'diy' },
    { task: 'Full Service', interval: 'Every 3 years', type: 'contractor' },
  ],
  water_softener: [
    { task: 'Salt Level Check & Refill', interval: 'Monthly', type: 'auto_order' },
    { task: 'Brine Tank Cleaning', interval: 'Annually', type: 'diy' },
    { task: 'Resin Bed Cleaning', interval: 'Every 2 years', type: 'diy' },
    { task: 'System Settings Check', interval: 'Annually', type: 'diy' },
  ],
  radiant_heat: [
    { task: 'Boiler Inspection & Service', interval: 'Annually', type: 'contractor' },
    { task: 'Bleed Radiators', interval: 'Annually — Fall', type: 'diy' },
    { task: 'Check Expansion Tank', interval: 'Annually', type: 'diy' },
    { task: 'Inspect Zone Valves', interval: 'Every 2 years', type: 'contractor' },
  ],
  electrical_panel: [
    { task: 'Visual Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Test GFCI & AFCI Breakers', interval: 'Every 6 months', type: 'diy' },
    { task: 'Professional Panel Inspection', interval: 'Every 5 years', type: 'contractor' },
    { task: 'Thermal Imaging Scan', interval: 'Every 5 years', type: 'contractor' },
  ],
  fireplace: [
    { task: 'Annual Inspection', interval: 'Annually', type: 'contractor' },
    { task: 'Chimney / Flue Cleaning', interval: 'Annually', type: 'contractor' },
    { task: 'Glass & Gasket Check', interval: 'Annually', type: 'diy' },
  ],
  fire_sprinkler: [
    { task: 'Annual Inspection', interval: 'Annually', type: 'contractor' },
    { task: 'Quarterly Visual Check', interval: 'Every 3 months', type: 'diy' },
    { task: 'Flow Test', interval: 'Annually', type: 'contractor' },
  ],
  air_filtration: [
    { task: 'Filter Replacement', interval: 'Every 3 months', type: 'auto_order' },
    { task: 'Annual Service', interval: 'Annually', type: 'contractor' },
  ],
  adu: [
    { task: 'General Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Smoke & CO Detector Test', interval: 'Monthly', type: 'diy' },
    { task: 'HVAC Filter Check', interval: 'Every 90 days', type: 'auto_order' },
    { task: 'Plumbing Inspection', interval: 'Annually', type: 'contractor' },
  ],
  other: [
    { task: 'General Inspection', interval: 'Annually', type: 'diy' },
    { task: 'Cleaning & Maintenance', interval: 'As needed', type: 'diy' },
  ],
  security_system: [
    { task: 'Test All Sensors & Alarms', interval: 'Every 6 months', type: 'diy' },
    { task: 'Replace Sensor Batteries', interval: 'Annually', type: 'diy' },
    { task: 'Verify Monitoring Connection', interval: 'Every 6 months', type: 'diy' },
    { task: 'Professional System Inspection', interval: 'Annually', type: 'contractor' },
  ],
  security_cameras: [
    { task: 'Clean Camera Lenses', interval: 'Every 3 months', type: 'diy' },
    { task: 'Check Camera Angles & Coverage', interval: 'Every 6 months', type: 'diy' },
    { task: 'Review & Purge Footage Storage', interval: 'Monthly', type: 'diy' },
    { task: 'Test Night Vision & Motion Detection', interval: 'Every 6 months', type: 'diy' },
  ],
  smart_doorbell: [
    { task: 'Clean Camera Lens', interval: 'Every 3 months', type: 'diy' },
    { task: 'Test Motion & Alert Settings', interval: 'Every 6 months', type: 'diy' },
    { task: 'Check Battery or Power Connection', interval: 'Annually', type: 'diy' },
  ],
  smart_locks: [
    { task: 'Replace Batteries', interval: 'Every 6 months', type: 'diy' },
    { task: 'Test Lock & Unlock Remotely', interval: 'Monthly', type: 'diy' },
    { task: 'Update Access Codes', interval: 'Annually', type: 'diy' },
  ],
  smoke_co_detectors: [
    { task: 'Test All Detectors', interval: 'Monthly', type: 'diy' },
    { task: 'Replace Batteries', interval: 'Every 6 months', type: 'diy' },
    { task: 'Replace Smoke Detectors', interval: 'Every 10 years', type: 'diy' },
    { task: 'Replace CO Detectors', interval: 'Every 7 years', type: 'diy' },
    { task: 'Check for Dust Buildup', interval: 'Every 6 months', type: 'diy' },
  ],
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear - i)

export default function AddSystem({ userId, propertyId, onClose, onSaved }) {
  const [step, setStep] = useState(1)
  const [selectedType, setSelectedType] = useState(null)
  const [focus, setFocus] = useState('')
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [form, setForm] = useState({
    manufacturer: '', model: '', installed_year: '',
    last_serviced: '', notes: '',
  })

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }))
    setAlert(null)
  }

  const selectedSystem = systemTypes.find(s => s.id === selectedType)
  const schedule = selectedType ? systemSchedules[selectedType] : []

  const tagColors = {
    auto_order: { bg: '#FBF3E8', color: '#B8721F', label: 'Order Parts' },
    contractor: { bg: '#EBF5EF', color: '#2E6B4A', label: 'Contractor' },
    diy: { bg: '#F2EDE4', color: '#9A7B68', label: 'DIY' },
  }

  const handleSave = async () => {
    setLoading(true)
    setAlert(null)
    const { error } = await supabase.from('systems').insert({
      user_id: userId,
      property_id: propertyId,
      type: selectedType,
      name: selectedSystem.name,
      manufacturer: form.manufacturer,
      model: form.model,
      installed_year: form.installed_year ? parseInt(form.installed_year) : null,
      last_serviced: form.last_serviced,
      notes: form.notes,
      status: 'good',
    })
    if (error) {
      setAlert({ type: 'error', msg: 'Error saving system. Please try again.' })
      setLoading(false)
    } else {
      onSaved()
    }
  }

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <div style={S.title}>
            {step === 1 ? 'Add Home System' : step === 2 ? 'System Details' : 'Maintenance Schedule'}
          </div>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={S.body}>
          <div style={S.stepBar}>
            {[1, 2, 3].map(s => (
              <div key={s} style={S.stepBarItem(s <= step)} />
            ))}
          </div>

          {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

          {/* STEP 1 — Select system type */}
          {step === 1 && (
            <div>
              <div style={S.sectionTitle}>Select System Type</div>
              <div style={S.typeGrid}>
                {systemTypes.map(s => {
                  const SIcon = s.Icon
                  return (
                    <div
                      key={s.id}
                      style={S.typeCard(selectedType === s.id)}
                      onClick={() => {
                        if (s.id === 'adu') return
                        setSelectedType(s.id)
                        setAlert(null)
                        setStep(2)
                      }}
                    >
                      <div style={S.typeIcon}><SIcon size={24} strokeWidth={1.5} color={selectedType === s.id ? C.amber : C.muted} /></div>
                      <div style={S.typeName(selectedType === s.id)}>{s.name}</div>
                      {s.badge && <div style={S.aduBadge}>{s.badge}</div>}
                    </div>
                  )
                })}
              </div>
              <button style={S.cancelBtn} onClick={onClose}>Cancel</button>
            </div>
          )}

          {/* STEP 2 — Details */}
          {step === 2 && (
            <div>
              {(() => { const SI = selectedSystem.Icon; return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '20px', padding: '12px 14px',
                background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`,
              }}>
                <SI size={28} strokeWidth={1.5} color={C.amber} />
                <div style={{ fontSize: '16px', fontWeight: '700', color: C.brown }}>{selectedSystem.name}</div>
              </div>
              ) })()}

              {selectedType === 'adu' && (
                <div style={S.aduInfoBox}>
                  <strong>ADU Management is coming soon.</strong> Full ADU tracking — separate appliances, systems, tenant info, and rental income management — is on the Casavio roadmap. For now your ADU will be tracked with basic maintenance reminders.
                </div>
              )}

              <div style={S.sectionTitle}>System Information</div>

              <div style={S.row}>
                <div style={S.field}>
                  <label style={S.label}>Manufacturer <span style={{ color: C.muted }}>(optional)</span></label>
                  <input
                    style={S.input(focus === 'manufacturer')}
                    placeholder="e.g. Tesla, Rheem"
                    value={form.manufacturer}
                    onChange={set('manufacturer')}
                    onFocus={() => setFocus('manufacturer')}
                    onBlur={() => setFocus('')}
                    autoComplete="off"
                  />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Model <span style={{ color: C.muted }}>(optional)</span></label>
                  <input
                    style={S.input(focus === 'model')}
                    placeholder="e.g. Powerwall 2"
                    value={form.model}
                    onChange={set('model')}
                    onFocus={() => setFocus('model')}
                    onBlur={() => setFocus('')}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div style={S.row}>
                <div style={S.field}>
                  <label style={S.label}>Year Installed <span style={{ color: C.muted }}>(optional)</span></label>
                  <select
                    style={S.select(focus === 'installed_year')}
                    value={form.installed_year}
                    onChange={set('installed_year')}
                    onFocus={() => setFocus('installed_year')}
                    onBlur={() => setFocus('')}
                  >
                    <option value="">Select year...</option>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Last Serviced <span style={{ color: C.muted }}>(optional)</span></label>
                  <input
                    style={S.input(focus === 'last_serviced')}
                    placeholder="e.g. March 2024"
                    value={form.last_serviced}
                    onChange={set('last_serviced')}
                    onFocus={() => setFocus('last_serviced')}
                    onBlur={() => setFocus('')}
                    autoComplete="off"
                  />
                </div>
              </div>

              <label style={S.label}>Notes <span style={{ color: C.muted }}>(optional)</span></label>
              <textarea
                style={S.textarea(focus === 'notes')}
                placeholder="e.g. Tank is 1,000 gallons, located in back yard near fence."
                value={form.notes}
                onChange={set('notes')}
                onFocus={() => setFocus('notes')}
                onBlur={() => setFocus('')}
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={{ ...S.cancelBtn, marginTop: 0, flex: 1 }} onClick={() => setStep(1)}>Back</button>
                <button style={{ ...S.btn(false), flex: 2, marginTop: 0 }} onClick={() => setStep(3)}>Continue</button>
              </div>
            </div>
          )}

          {/* STEP 3 — Schedule review */}
          {step === 3 && (
            <div>
              {(() => { const SI = selectedSystem.Icon; return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '16px', padding: '12px 14px',
                background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`,
              }}>
                <SI size={24} strokeWidth={1.5} color={C.amber} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown }}>{selectedSystem.name}</div>
                  {form.manufacturer && (
                    <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                      {form.manufacturer}{form.model ? ' · ' + form.model : ''}
                      {form.installed_year ? ' · Installed ' + form.installed_year : ''}
                    </div>
                  )}
                </div>
              </div>
              ) })()}

              <div style={S.sectionTitle}>Maintenance Schedule — Generated Automatically</div>
              <div style={{ marginBottom: '20px' }}>
                {schedule.map((item, i) => {
                  const tag = tagColors[item.type]
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0',
                      borderBottom: i < schedule.length - 1 ? `1px solid ${C.border}` : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: C.brown }}>{item.task}</div>
                        <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' }}>{item.interval}</div>
                      </div>
                      <div style={{
                        fontSize: '10px', padding: '3px 10px', borderRadius: '20px',
                        background: tag.bg, color: tag.color,
                        fontFamily: 'Calibri, sans-serif', letterSpacing: '0.5px',
                        border: `1px solid ${tag.color}44`, whiteSpace: 'nowrap',
                      }}>
                        {tag.label}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px', lineHeight: 1.6, textAlign: 'center', fontStyle: 'italic' }}>
                Casavio will remind you, order parts, and send contractor bids automatically.
              </div>

              <button style={S.btn(loading)} onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save System'}
              </button>
              <button style={S.cancelBtn} onClick={() => setStep(2)}>Back</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}